import { useCallback, useMemo, useRef, useState } from 'react'
import {
  getPercritorMemed,
  savePrescriptionMemed,
} from 'containers/MedicalRecord/New/services'
import { translate } from 'locales'
import { alertDanger, alertSuccess } from 'utils/toast'
import {
  closeMemedPrescricao,
  MemedPatient,
  openMemedPrescricao,
} from './index'
import { format, isValid, parseISO } from 'date-fns'

type UseMemedParams = {
  appointment: any
  userData: any
  onPrescriptionSaved?: () => void
}

export const useMemed = ({
  appointment,
  userData,
  onPrescriptionSaved,
}: UseMemedParams) => {
  const [active, setActive] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const pendingOpenRef = useRef(false)

  const isValidBirthdate = (date?: string) => {
    if (!date) return false
    const parsedDate = parseISO(date)
    return isValid(parsedDate)
  }

  const formatDate = (date: string) => {
    return format(parseISO(date), 'dd/MM/yyyy')
  }

  const patient = useMemo<MemedPatient | undefined>(() => {
    if (!appointment?.user_id) return undefined

    const birthdateIsValid = isValidBirthdate(userData?.birthdate)

    return {
      idExterno: String(appointment.user_id),
      nome: appointment?.user_name || userData?.name || '',
      cpf: userData?.document,
      email: userData?.email,
      data_nascimento: birthdateIsValid ? formatDate(userData?.birthdate) : '',
      telefone: appointment?.cellphone || userData?.cellphone,
      nome_social: appointment?.user_social_name || userData?.social_name,
      sexo: userData?.gender === 'male' ? 'M' : 'F',
    }
  }, [appointment, userData])

  const open = useCallback(async () => {
    if (active && ready) {
      openMemedPrescricao()
      setVisible(true)
      return
    }
    if (active && !ready) {
      pendingOpenRef.current = true
      setLoading(true)
      return
    }
    setLoading(true)
    try {
      const prescriber = await getPercritorMemed(appointment?.doctor_id)
      if (!prescriber?.token) {
        alertDanger('Erro ao obter o token do Memed')
        setLoading(false)
        return
      }
      pendingOpenRef.current = true
      setToken(prescriber.token)
      setActive(true)
    } catch {
      alertDanger('Erro ao obter o token do Memed')
      setLoading(false)
    }
  }, [active, ready, appointment?.doctor_id])

  const close = useCallback(() => {
    closeMemedPrescricao()
    setVisible(false)
  }, [])

  const handleClose = useCallback(() => {
    setVisible(false)
  }, [])

  const handleModuleReady = useCallback(() => {
    setReady(true)
    if (pendingOpenRef.current) {
      pendingOpenRef.current = false
      openMemedPrescricao()
      setVisible(true)
      setLoading(false)
    }
  }, [])

  const handlePrescricaoSalva = useCallback(
    async (prescriptionId: string, rawData?: any) => {
      if (!token) return
      await savePrescriptionMemed(prescriptionId, {
        token,
        user_id: appointment?.user_id,
        doctor_id: appointment?.doctor_id,
        appointment_id: appointment?.id,
        full_payload: rawData?.prescricao,
      })
      onPrescriptionSaved?.()
      alertSuccess(translate('medicalRecord.finishedSuccess'))
    },
    [token, appointment, onPrescriptionSaved]
  )

  return {
    active,
    token,
    visible,
    loading,
    patient,
    open,
    close,
    handleClose,
    handleModuleReady,
    handlePrescricaoSalva,
  }
}
